import { cn } from "@/lib/cn";
import type { StaticEntityType } from "@config";
import { useIsPdf } from "@library/components";
import { type CSSProperties, useState } from "react";
import { SandboxedHtml } from "../../embedded/components/SandboxedHtml";
import { REGEX } from "../constants";
import { EmbeddedStyled } from "../styles/Form.styled";
import type { EditorDataProps, EditorSlotsProps, FormProps } from "../types";
import { Preview } from "./Preview";

export const FormLibrary = ({
  entity,
  editorSlots,
  editorData,
}: {
  entity: StaticEntityType;
  editorSlots?: EditorSlotsProps;
  editorData?: EditorDataProps;
}) => {
  const isPdf = useIsPdf();
  const settings = entity.settings as FormProps;
  const width = editorData?.width || settings.width;
  const height = editorData?.height || settings.height;

  const [active, setActive] = useState<string>(
    settings.code.startsWith("http") ? "link" : "code",
  );

  const [prevCode, setPrevCode] = useState(settings.code);
  if (settings.code !== prevCode) {
    setPrevCode(settings.code);
    setActive(settings.code.startsWith("http") ? "link" : "code");
  }

  return (
    <EmbeddedStyled
      $horizontalAlignment={settings.horizontalAlignment}
      style={
        {
          "--entity-width": `${width}%`,
          "--entity-height": `${settings.view === "preview" || isPdf ? "auto" : height + "px"}`,
        } as CSSProperties
      }
      ref={editorData?.containerRef}
      className="tw-relative tw-flex"
    >
      {editorSlots?.resizeSlot}
      <div
        className="iframe__wrapper tw-overflow-hidden max-md:tw-w-full"
        {...EDITOR_ATTRIBUTES({ isEditor: !!editorData })}
      >
        {(settings.view === "preview" && settings.type !== "custom") ||
        isPdf ? (
          <Preview entity={entity} />
        ) : (
          <>
            {active === "link" && REGEX.test(settings.code) && (
              <iframe
                src={settings.code}
                title="URL link"
                className={cn("tw-h-full tw-w-full tw-border-none", {
                  "tw-pointer-events-none": !!editorSlots,
                })}
              ></iframe>
            )}
            {active === "code" && settings.code && (
              <SandboxedHtml
                code={settings.code}
                title="Embedded form"
                className={cn("code tw-h-full tw-w-full tw-border-none", {
                  "tw-pointer-events-none": !!editorSlots,
                })}
              />
            )}
            {!settings.code && editorSlots?.emptySlot}
          </>
        )}
      </div>
    </EmbeddedStyled>
  );
};

const EDITOR_ATTRIBUTES = ({ isEditor }: { isEditor: boolean }) => {
  const payload: Record<string, string> = {
    "data-dnd-trigger": "true",
  };
  return isEditor ? payload : {};
};
